import * as THREE from 'three';
import { Vector3, Color, Vector2 } from '../types';
import { SceneNode } from '../core/SceneNode';
import { POI } from '../poi/POI';
import { Scene } from '../core/Scene';

export class MeshPointIndicator extends SceneNode {
    private _poi: POI;
    private _circle: THREE.Mesh | null = null;
    private _material: THREE.MeshBasicMaterial | null = null;
    protected _geometry: THREE.CircleGeometry | null = null;
    private _canvas: HTMLCanvasElement | null = null;
    private _canvasTexture: THREE.CanvasTexture | null = null;
    private _needsUpdate: boolean = true;

    constructor(poi: POI, name: string = 'MeshPointIndicator') {
        super(name);
        this._poi = poi;
        this.buildMesh();
    }

    private cleanupMesh(): void {
        if (this._circle && this._circle.parent) {
            this._circle.parent.remove(this._circle);
        }
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        if (this._canvasTexture) {
            this._canvasTexture.dispose();
            this._canvasTexture = null;
        }
        this._canvas = null;
        this._circle = null;
    }

    protected buildMesh(): void {
        this.cleanupMesh();
        
        // Create circle geometry
        this._geometry = new THREE.CircleGeometry(0.1, 16); // Small circle, 16 segments
        
        // Create canvas for custom rendering
        this._canvas = document.createElement('canvas');
        this._canvas.width = 64;
        this._canvas.height = 64;
        
        // Create texture from canvas
        this._canvasTexture = new THREE.CanvasTexture(this._canvas);
        this._canvasTexture.minFilter = THREE.LinearFilter;
        this._canvasTexture.magFilter = THREE.LinearFilter;
        
        // Create material
        this._material = new THREE.MeshBasicMaterial({
            map: this._canvasTexture,
            transparent: true,
            alphaTest: 0.1,
            side: THREE.DoubleSide
        });
        
        // Create mesh
        this._circle = new THREE.Mesh(this._geometry, this._material);
        this._circle.userData.sceneNode = this;
        this._mesh = this._circle;
        
        // Initial update
        this.updateIndicator();
    }

    private updateIndicator(): void {
        if (!this._canvas || !this._canvasTexture) return;
        
        const ctx = this._canvas.getContext('2d');
        if (!ctx) return;
        
        // Clear canvas
        ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
        
        // Get settings
        const showNumbers = this._poi.poidata.showMeshPointNumbers?.value ?? false;
        const indicatorColor = this._poi.poidata.meshPointColor?.value ?? new Color('#ff6b6b');
        const backgroundColor = this._poi.poidata.meshPointBackgroundColor?.value ?? new Color('#ffffff');
        const labelNr = this._poi.poidata.labelNr?.value ?? 1;
        
        const centerX = this._canvas.width / 2;
        const centerY = this._canvas.height / 2;
        const radius = 28;
        
        if (showNumbers) {
            // Draw circle with background and number
            ctx.fillStyle = backgroundColor.toString();
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.fill();
            
            // Draw border
            ctx.strokeStyle = indicatorColor.toString();
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.stroke();
            
            // Draw number
            ctx.fillStyle = indicatorColor.toString();
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(labelNr.toString(), centerX, centerY);
        } else {
            // Draw solid circle
            ctx.fillStyle = indicatorColor.toString();
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.fill();
        }
        
        // Update texture
        this._canvasTexture.needsUpdate = true;
        this._needsUpdate = false;
    }

    public onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);
        
        if (this._needsUpdate) {
            this.updateIndicator();
        }
        
        // Billboard behavior - always face camera
        this.updateBillboard();
        
        // Update position to match mesh point
        this.updatePosition();
    }

    private updateBillboard(): void {
        if (!this._circle) return;
        
        const camera = Scene.Instance.getCamera();
        if (!camera) return;
        
        // Make the indicator always face the camera
        const cameraPosition = new THREE.Vector3();
        camera.getWorldPosition(cameraPosition);
        
        const indicatorPosition = new THREE.Vector3();
        this._circle.getWorldPosition(indicatorPosition);
        
        this._circle.lookAt(cameraPosition);
    }

    private updatePosition(): void {
        if (!this._circle) return;
        
        const meshPoint = this._poi.poidata.labelMeshPoint?.value;
        if (!meshPoint) return;
        
        // Use POI shape's accurate UV-to-3D mapping
        if (!this._poi.shape) {
            console.warn('MeshPointIndicator: POI shape not available');
            return;
        }
        
        try {
            const worldPosition = this._poi.shape.getPositionFromUV(new Vector2(meshPoint.x, meshPoint.y));
            this.transform.setWorldPosition(worldPosition);
        } catch (error) {
            console.warn('MeshPointIndicator: Failed to get position from UV:', error);
            // Fallback to POI center
            this.transform.setWorldPosition(this._poi.transform.getWorldPosition());
        }
    }

    public triggerUpdate(): void {
        this._needsUpdate = true;
    }

    protected onDestroy(): void {
        if (this._circle && this._circle.parent) {
            this._circle.parent.remove(this._circle);
        }
        
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        
        if (this._canvasTexture) {
            this._canvasTexture.dispose();
            this._canvasTexture = null;
        }
        
        this._canvas = null;
        this._circle = null;
        
        super.onDestroy();
    }

    protected createGeometry(): THREE.BufferGeometry {
        return new THREE.CircleGeometry(0.1, 16);
    }

    protected calculateBoundingBox(): THREE.Box3 {
        if (!this._circle) return new THREE.Box3();
        
        this._circle.updateWorldMatrix(true, false);
        const box = new THREE.Box3().setFromObject(this._circle);
        return box;
    }
} 